/**
 *
 * Source: https://wix-ux.com/when-life-gives-you-lemons-write-better-error-messages-46c5223e1a2f
 * Good error message example:
 *
 *
 *             │ Say what happened           │Say why it happened
 *             │ in title                    │
 *             │                             │
 *             ▼                             │
 *        Unable to connect your Account     │
 *       ┌────────────────────────┐ ┌────────▼─────────────────────────────────────────────────────────────────┐
 * ┌────►│Your changes were saved,│ │but we could not connect your account due to a technical issue on our end.│
 * │     ├────────────────────────┴─┴─┬─┬───────────────────────────────────────────────────────┬──────────────┘
 * │     │Please try connecting again.│ │If the issue keeps happening, contact customer support.│
 * │     └────────────────▲───────────┘ └──────────▲────────────────────────────────────────────┘
 * │                      │                        │
 * │                      │                        │
 * Provide reassurance    Help them fix it         Give them a way out
 *
 */
const contactSupport = 'If the issue keeps happening, contact your customer success manager for further assistance.';
const builderServer =
  'We encountered an issue while generating or updating your AJS file. This may be a temporary issue with our builder server. Please refresh the page and try again.';
const apolloIssues =
  'It looks like something went wrong while contacting our servers. Please refresh the page and try again.';

export default {
  // region network, these errors happen when there are timeouts, or when we cannot connect to the Apollo server
  networkIssues: {
    title: 'Connection issues',
    message:
      'We are unable to connect to our servers. Please refresh the page. If you continue to run into errors, please reach out to your customer success manager.',
  },
  // endregion

  // region graphQL These errors happen when we connect to external APIs, control proxy, or builder server
  graphQL: {
    default: {
      title: 'Unable to send data to server',
      message: `We could not connect to the server to perform the requested action. Please refresh the page. ${contactSupport}`,
    },
    invalidJWT: {
      title: 'Invalid credentials',
      message:
        'Unable to retrieve your credentials. Please log out and try again. If the issue keeps happening, please contact your customer success manager.',
    },
    expiredJWT: {
      title: 'Your session has expired',
      message: 'Your session ended due to a lack of activity. Please sign in again.',
    },
    permissionDenied: {
      title: 'You do not have the proper permissions to perform this action.',
      message:
        'If you feel you should be able to perform this action, please contact your organization admin(s) for further assistance.',
    },
    // https://www.apollographql.com/docs/apollo-server/v2/data/errors/#graphql_validation_failed
    validationFailed: {
      title: 'We encountered an issue loading your data.',
      message:
        'This may be due to a temporary system issue or an unexpected query error. Refresh the page and try again. If the issue keeps happening, please contact your customer success manager.',
    },
    userControlProxyErrors: {
      title: (action = 'unknown', resource = 'unknown', writekey = 'unknown') => {
        return `Unable to ${action} ${resource} for writekey ${writekey}`;
      },
      message: `${apolloIssues} ${contactSupport}`,
    },
    // region builder server
    ClientSideIntegrations: {
      title: 'There was an error while grabbing client-side integrations.',
      message: `${builderServer} ${contactSupport}`,
    },
    IdentitySyncs: {
      title: 'There was an error while grabbing available identity syncs.',
      message: `${builderServer} ${contactSupport}`,
    },
    BuildAjsFile: {
      title: 'There was an error while generating your AJS file.',
      message: `${builderServer} ${contactSupport}`,
    },
    RemoveAnalyticsFile: {
      title: 'There was an error while removing your AJS file.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpsertAnalyticsFile: {
      title: 'There was an error while updating your AJS file.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
    // region clusters
    CreateCluster: {
      title: 'There was an error while creating your cluster.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    RemoveCluster: {
      title: 'There was an error while removing your cluster.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
    // region integrations
    InsertIntegration: {
      title: 'There was an error while creating your integration.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    RemoveIntegration: {
      title: 'There was an error while removing your integration.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpdateIntegrationName: {
      title: 'There was an error while updating your integration.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpsertIntegrationNotesOne: {
      title: 'There was an error while updating your integration notes.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
    // region organizations
    InsertOrg: {
      title: 'There was an error while creating your organization.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    RemoveOrg: {
      title: 'There was an error while removing your organization.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    SwitchOrg: {
      title: 'There was an error while switching organizations.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpdateOrg: {
      title: 'There was an error while updating your organization.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
    // region pipelines
    CreatePipeline: {
      title: 'There was an error while creating your pipeline.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    CreateStagedRevision: {
      title: 'There was an error while staging your integration revision.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    RemovePipeline: {
      title: 'There was an error while removing your pipeline.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    RemoveStagedRevision: {
      title: 'There was an error while removing your staged integration revision.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpdatePipeline: {
      title: 'There was an error while updating your pipeline.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpdateStagedRevision: {
      title: 'There was an error while updating your staged integration revisions.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
    // region playbooks
    CreatePlaybookRevision: {
      title: 'There was an error while creating your integration revision.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
    // region schemas
    DiffSchemas: {
      title: 'There was an error while creating your schema diff view.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    RemoveEventSchema: {
      title: 'There was an error while removing your event schema.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpsertEventSchema: {
      title: 'There was an error while creating or updating your event schema.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
    // region users
    RemoveOrgUser: {
      title: 'There was an error while removing a user from your organization.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    InsertOrgUsers: {
      title: 'There was an error while adding a user to your organization.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpdateMe: {
      title: 'There was an issue while updating your user profile.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    UpdateOrgUser: {
      title: 'There was an error while updating a user profile.',
      message: `${apolloIssues} ${contactSupport}`,
    },
    // endregion
  },
  // endregion

  // role
  role: {
    title: 'No role assigned',
    message:
      'You are a member of this organization, but do not yet have a "role" assigned. Please contact your organization admin(s) for further assistance, or contact your customer success manager.',
  },
};
